import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  ScrollArea,
} from '@wove/web-ui'
import { PencilIcon, Loader2 } from 'lucide-react'
import { DocumentField, DocumentObjectField, DocumentArrayField } from '../components/DocumentFields'
import { DocumentHistoryEntry } from '../components/DocumentHistoryEntry'
import { useDocumentHistory } from '../hooks/useDocumentHistory'
import { useUpdateDocument } from '../hooks/useUpdateDocument'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'

export interface DocumentSection {
  id: string
  title: string
  layout: 'single' | 'double' | 'grid'
  fields: DocumentSectionField[]
}

export interface DocumentSectionField {
  type: 'field' | 'object' | 'array'
  label?: string
  path: string
  fields?: {
    label: string
    path: string
    render?: (value: any) => string
    complexType?: 'weight' | 'volume' | 'dimensions' | 'currency'
  }[]
  columns?: {
    label: string
    path: string
    render?: (value: any) => string
    complexType?: 'weight' | 'volume' | 'dimensions' | 'currency'
  }[]
  optional?: boolean
  fullWidth?: boolean
  complexType?: 'weight' | 'volume' | 'dimensions' | 'currency'
}

export interface DocumentDialogProps {
  documentData: any
  schema: any
  sections: DocumentSection[]
  documentType: string
  onSave?: (updatedData: any, changes: Array<{ path: string; operation: string; oldValue: any; newValue: any }>) => void
  additionalTabs?: {
    id: string
    label: string
    content: React.ReactNode
  }[]
  shipmentId: string
  documentId: string
}

const DocumentDialog: React.FC<DocumentDialogProps> = ({
  documentData,
  schema,
  sections,
  documentType,
  onSave,
  additionalTabs = [],
  shipmentId,
  documentId,
}) => {
  const [editMode, setEditMode] = useState(false)
  const historyResult = useDocumentHistory(shipmentId, documentId)
  const queryClient = useQueryClient()

  // Add a ref to track changes as they happen
  const changesRef = useRef<Array<{ path: string; operation: string; oldValue: any; newValue: any }>>([])

  // Reset changes when entering edit mode
  useEffect(() => {
    if (editMode) {
      changesRef.current = []
    }
  }, [editMode])

  // Set up react-hook-form without zodResolver to avoid type depth issues
  const form = useForm<any>({
    defaultValues: documentData,
    mode: 'onChange',
  })

  // Track field changes
  const trackChange = useCallback((path: string, operation: string, oldValue: any, newValue: any) => {
    // Don't record if values are the same for updates
    if (operation === 'update' && JSON.stringify(oldValue) === JSON.stringify(newValue)) {
      return
    }

    // Check if we already have a change for this path
    const existingChangeIndex = changesRef.current.findIndex(
      change => change.path === path && change.operation === operation,
    )

    if (existingChangeIndex >= 0) {
      // Update the existing change (keep original oldValue, update newValue)
      changesRef.current[existingChangeIndex].newValue = newValue
    } else {
      // Add a new change
      changesRef.current.push({
        path,
        operation,
        oldValue,
        newValue,
      })
    }

    console.log('Current changes:', changesRef.current)
  }, [])

  // Reset form when data changes
  useEffect(() => {
    form.reset(documentData)
  }, [documentData, form])

  const saveUpdatedDocument = useUpdateDocument(shipmentId, documentId)

  // Subscribe to form changes
  useEffect(() => {
    if (!editMode) {
      return
    }

    const subscription = form.watch((value, { name, type }) => {
      if (!name) return // Skip if no field name

      console.log('Watch triggered:', {
        name,
        type,
        documentData,
        value,
      })

      // Get the old value from the original data
      const getValueAtPath = (obj: any, path: string) => {
        if (!obj) {
          console.log('Source object is null/undefined:', obj)
          return undefined
        }

        const parts = path.split('.')
        let current = obj

        for (const part of parts) {
          if (current === undefined || current === null) {
            console.log('Path traversal failed at:', part)
            return undefined
          }

          current = current[part]
        }

        return current
      }

      const oldValue = getValueAtPath(documentData, name)
      const newValue = getValueAtPath(value, name)

      console.log('Values:', { oldValue, newValue })

      // Track the change based on the type
      if (type === 'change') {
        trackChange(name, 'update', oldValue, newValue)
      }
    })

    return () => subscription.unsubscribe()
  }, [editMode, form, documentData, trackChange])

  // Add methods to track array operations
  const trackArrayItemAdded = useCallback(
    (arrayPath: string, newItem: any, index: number) => {
      trackChange(`${arrayPath}.${index}`, 'add', undefined, newItem)
    },
    [trackChange],
  )

  const trackArrayItemRemoved = useCallback(
    (arrayPath: string, oldItem: any, index: number) => {
      trackChange(`${arrayPath}.${index}`, 'remove', oldItem, undefined)
    },
    [trackChange],
  )

  const trackArrayItemMoved = useCallback(
    (arrayPath: string, item: any, oldIndex: number, newIndex: number) => {
      trackChange(`${arrayPath}`, 'move', { index: oldIndex, value: item }, { index: newIndex, value: item })
    },
    [trackChange],
  )

  const handleSave = (formData: any) => {
    console.log('FORM DATA', formData)
    console.log('TRACKED CHANGES', changesRef.current)

    if (onSave) {
      onSave(formData, changesRef.current)
    }

    saveUpdatedDocument.mutate(
      {
        data: formData,
        changes: changesRef.current,
      },
      {
        onSuccess(data, variables, context) {
          console.log('SUCCESS', data, variables, context)
          setEditMode(false)
          historyResult.refetch()
          queryClient.invalidateQueries({ queryKey: ['document', shipmentId, documentId] })
          toast.success('Document updated successfully')
        },
        onError(error, variables, context) {
          console.log('ERROR', error, variables, context)
          toast.error('Failed to update document. Please try again.')
        },
      },
    )
  }

  // Modify the DocumentArrayField component to use the tracking functions
  const enhancedRenderSection = (section: DocumentSection) => {
    const { id, title, layout, fields } = section

    return (
      <Card key={id} className="shadow-sm">
        <CardHeader className="py-2 px-4">
          <CardTitle className="text-md">{title}</CardTitle>
        </CardHeader>
        <CardContent className="py-2 px-4">
          <div
            className={
              layout === 'single'
                ? 'grid grid-cols-1 gap-1 text-sm'
                : layout === 'double'
                ? 'grid grid-cols-2 gap-1 text-sm'
                : 'grid grid-cols-1 md:grid-cols-2 gap-3'
            }
          >
            {fields.map((field, index) => {
              const { type, label, path, fields: subFields, columns, fullWidth } = field
              const optional = true

              const getSchemaForPath = (schemaObj: any, fieldPath: string) => {
                if (!schemaObj || !fieldPath) return schemaObj // Return current schema if path is empty

                const pathParts = fieldPath.split('.')
                let currentSchema = schemaObj

                for (const part of pathParts) {
                  if (!currentSchema.properties || !currentSchema.properties[part]) {
                    return undefined
                  }
                  currentSchema = currentSchema.properties[part]
                }

                return currentSchema
              }

              const fieldSchema = getSchemaForPath(schema, path)

              if (type === 'field') {
                return (
                  <DocumentField
                    key={`${id}-field-${index}`}
                    label={label}
                    path={path}
                    isEditing={editMode}
                    schema={fieldSchema}
                    fullWidth={fullWidth}
                    complexType={field.complexType}
                  />
                )
              } else if (type === 'object') {
                return (
                  <DocumentObjectField
                    key={`${id}-object-${index}`}
                    label={label}
                    path={path}
                    isEditing={editMode}
                    schema={fieldSchema}
                    fields={subFields}
                    optional={optional}
                  />
                )
              } else if (type === 'array') {
                return (
                  <DocumentArrayField
                    key={`${id}-array-${index}`}
                    label={label}
                    path={path}
                    isEditing={editMode}
                    schema={fieldSchema}
                    columns={columns}
                    optional={optional}
                    onItemAdded={(item, idx) => trackArrayItemAdded(path, item, idx)}
                    onItemRemoved={(item, idx) => trackArrayItemRemoved(path, item, idx)}
                    onItemMoved={(item, oldIdx, newIdx) => trackArrayItemMoved(path, item, oldIdx, newIdx)}
                  />
                )
              }
              return null
            })}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <FormProvider {...form}>
      <Tabs defaultValue="document" className="w-full h-full flex flex-col">
        <div className="flex justify-between items-center mb-2">
          <TabsList>
            <TabsTrigger value="document">{documentType}</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            {additionalTabs.map(tab => (
              <TabsTrigger key={tab.id} value={tab.id}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {!editMode ? (
            <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
              <PencilIcon className="h-4 w-4 mr-1" /> Edit
            </Button>
          ) : (
            <div className="space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  form.reset(documentData)
                  setEditMode(false)
                }}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={form.handleSubmit(handleSave, errors => {
                  console.log('ERRORS', errors)
                })}
                disabled={saveUpdatedDocument.isLoading}
              >
                {saveUpdatedDocument.isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Document Details Tab */}
        <TabsContent value="document" className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto">
            <form
              onSubmit={form.handleSubmit(handleSave, errors => {
                console.log('ERRORS', errors)
              })}
              className="space-y-3 p-2"
            >
              {sections.map(enhancedRenderSection)}
            </form>
          </div>
        </TabsContent>

        {/* History Tab - Update to show detailed changes */}
        <TabsContent value="history">
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle>Document History</CardTitle>
            </CardHeader>
            <CardContent className="py-2 px-4">
              <div className="space-y-3 text-sm">
                {historyResult.isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-500"></div>
                    <span className="ml-2 text-gray-500">Loading history...</span>
                  </div>
                ) : historyResult.error ? (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-600">
                    <p className="font-medium">Error loading history</p>
                    <p className="text-xs mt-1">{(historyResult.error as any)?.message || 'Please try again later.'}</p>
                  </div>
                ) : historyResult.data &&
                  Array.isArray(historyResult.data.history) &&
                  historyResult.data.history.length > 0 ? (
                  historyResult.data.history.map((entry: any, index: number) => (
                    <DocumentHistoryEntry key={index} entry={entry} />
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No history available for this document.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Additional Tabs */}
        {additionalTabs.map(tab => (
          <TabsContent key={tab.id} value={tab.id}>
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>
    </FormProvider>
  )
}

export default DocumentDialog
